import { useState } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface UpdateStatusOptions {
  appointmentId: string;
  newStatus: 'booked' | 'confirmed' | 'completed' | 'canceled' | 'no_show';
  reason?: string;
}

export function useAppointmentStatus() {
  const [loading, setLoading] = useState(false);

  const updateAppointmentStatus = async ({ appointmentId, newStatus, reason }: UpdateStatusOptions) => {
    try {
      setLoading(true);

      const { data, error } = await supabase.rpc('update_appointment_status_v2', {
        p_appointment_id: appointmentId,
        p_status: newStatus,
        p_reason: reason
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || 'שגיאה בעדכון סטטוס התור');
      }

      // הצגת הודעה מתאימה לפי סוג הסטטוס
      const statusMessages = {
        booked: 'התור הועבר למצב המתנה',
        confirmed: 'התור אושר בהצלחה',
        completed: 'התור סומן כהושלם',
        canceled: 'התור בוטל בהצלחה',
        no_show: 'התור סומן כלא הגיע'
      };

      toast.success(statusMessages[newStatus]);

      // אם התור הושלם, בדוק האם תוכנית הנאמנות פעילה לפני הצגת הודעה על נקודות
      if (newStatus === 'completed') {
        // שלוף את business_id של התור
        const { data: appointmentData, error: appointmentError } = await supabase
          .from('appointments')
          .select('business_id')
          .eq('id', appointmentId)
          .single();

        if (!appointmentError && appointmentData?.business_id) {
          // שלוף את הגדרת הנאמנות של העסק
          const { data: businessData, error: businessError } = await supabase
            .from('businesses')
            .select('settings')
            .eq('id', appointmentData.business_id)
            .single();

          const loyaltyEnabled =
            businessData?.settings?.loyalty?.enabled === true ||
            businessData?.settings?.loyalty_enabled === true;

          if (!businessError && loyaltyEnabled) {
            toast.success('נקודות הנאמנות עודכנו אוטומטית', {
              duration: 5000,
              icon: '🎁'
            });
          }
        }
      }

      return true;
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בעדכון סטטוס התור');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // הוסף פונקציה שבודקת האם ניתן לערוך תור לפי סטטוס
  const canEditAppointment = (status: string) => {
    // עריכה מותרת רק אם התור לא בסטטוס completed, canceled, no_show
    return status === 'booked' || status === 'confirmed';
  };

  return {
    loading,
    updateAppointmentStatus,
    canEditAppointment // תוכל להשתמש בזה בקומפוננטה
      };
}